import { supabase } from '../lib/supabase';

class FahrtenService {
  /**
   * Create a new fahrt entry
   */
  async createFahrt(fahrtData, userId) {
    try {
      const { data, error } = await supabase
        .from('fahrten')
        .insert([
          {
            vin: fahrtData.vin,
            vehicle_make: fahrtData.vehicle_make,
            vehicle_model: fahrtData.vehicle_model,
            fahrt_datum: fahrtData.fahrt_datum || new Date().toISOString().split('T')[0],
            fahrt_uhrzeit_beginn: fahrtData.fahrt_uhrzeit_beginn || new Date().toTimeString().slice(0, 5),
            fahrt_uhrzeit_ende: fahrtData.fahrt_uhrzeit_ende || null,
            startziel: fahrtData.startziel,
            ueber: fahrtData.ueber,
            endziel: fahrtData.endziel,
            fahrtzweck: fahrtData.fahrtzweck,
            fahrer_name: fahrtData.fahrer_name,
            fahrer_adresse: fahrtData.fahrer_adresse,
            notes: fahrtData.notes,
            status: fahrtData.status || 'pending',
            created_by: userId,
            metadata: fahrtData.metadata || {}
          }
        ])
        .select()
        .single();

      if (error) {
        console.error('Error creating fahrt:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating fahrt:', error);
      return { data: null, error };
    }
  }

  /**
   * Get all fahrten for a user
   */
  async getFahrten(userId, options = {}) {
    try {
      let query = supabase
        .from('fahrten')
        .select('*')
        .eq('created_by', userId)
        .order('created_at', { ascending: true });

      // Add filters if provided
      if (options.status) {
        query = query.eq('status', options.status);
      }

      if (options.dateFrom) {
        query = query.gte('fahrt_datum', options.dateFrom);
      }

      if (options.dateTo) {
        query = query.lte('fahrt_datum', options.dateTo);
      }

      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching fahrten:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching fahrten:', error);
      return { data: null, error };
    }
  }

  /**
   * Get a single fahrt by ID
   */
  async getFahrtById(fahrtId, userId) {
    try {
      const { data, error } = await supabase
        .from('fahrten')
        .select('*')
        .eq('id', fahrtId)
        .eq('created_by', userId)
        .single();

      if (error) {
        console.error('Error fetching fahrt:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching fahrt:', error);
      return { data: null, error };
    }
  }

  /**
   * Update a fahrt entry
   */
  async updateFahrt(fahrtId, updates, userId) {
    try {
      const { data, error } = await supabase
        .from('fahrten')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', fahrtId)
        .eq('created_by', userId)
        .select()
        .single();

      if (error) {
        console.error('Error updating fahrt:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating fahrt:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete a fahrt entry
   */
  async deleteFahrt(fahrtId, userId) {
    try {
      // Get the fahrt data before deletion to find the VIN
      const { data: fahrtData, error: fetchError } = await supabase
        .from('fahrten')
        .select('vin')
        .eq('id', fahrtId)
        .eq('created_by', userId)
        .single();

      if (fetchError) {
        console.error('Error fetching fahrt data before deletion:', fetchError);
      }

      // Delete the fahrt
      const { error } = await supabase
        .from('fahrten')
        .delete()
        .eq('id', fahrtId)
        .eq('created_by', userId);

      if (error) {
        console.error('Error deleting fahrt:', error);
        return { error };
      }

      // Update scan history to mark as not having order
      if (fahrtData?.vin) {
        try {
          // Import scanHistoryService dynamically to avoid circular dependencies
          const { default: scanHistoryService } = await import('./scanHistoryService.js');
          await scanHistoryService.updateScanOrderStatus(fahrtData.vin, userId, false);
          console.log('Updated scan history status for VIN:', fahrtData.vin);
        } catch (scanError) {
          console.error('Error updating scan history after fahrt deletion:', scanError);
        }
      }

      return { error: null };
    } catch (error) {
      console.error('Error deleting fahrt:', error);
      return { error };
    }
  }

  /**
   * Get fahrten statistics for a user
   */
  async getFahrtenStats(userId, dateFrom, dateTo) {
    try {
      let query = supabase
        .from('fahrten')
        .select('status, fahrt_datum')
        .eq('created_by', userId);

      if (dateFrom) {
        query = query.gte('fahrt_datum', dateFrom);
      }

      if (dateTo) {
        query = query.lte('fahrt_datum', dateTo);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching fahrten stats:', error);
        return { data: null, error };
      }

      // Calculate statistics
      const stats = {
        total: data.length,
        pending: data.filter(f => f.status === 'pending').length,
        completed: data.filter(f => f.status === 'completed').length,
        cancelled: data.filter(f => f.status === 'cancelled').length
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching fahrten stats:', error);
      return { data: null, error };
    }
  }
}

export const fahrtenService = new FahrtenService();
